import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@Auth/useAuth";
import { useDrawer } from "@hooks/useDrawer";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { onInfoShow } from "@utils/onInfoShow";
import { useLocations } from "@features/locations/queries/useLocations";
import {
  useCreateLocation,
  useUpdateLocation,
} from "@features/locations/queries/useLocationMutations";
import { buildParentOptions } from "@features/locations/utils/parentOptions";

/**
 * View-model for the create/edit location drawer. Owns the name + parent state,
 * edit/prefill population, validation, and the save path (including the
 * `createFromStockView` quick-create from the stock form). The parent options
 * exclude the location and its descendants so a folder can't be moved into its
 * own subtree.
 */
export function useLocationForm(createFromStockView: boolean) {
  const { context, selectedId, prefillLocationId, close } = useDrawer();
  const { setTouch } = useConfirmationModalStore();
  const { baseUser } = useAuth();
  const { data: locations = [] } = useLocations();
  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation();

  const isEdit = context === DRAWER_CONTEXT.EDIT_LOCATION;

  const [name, setNameValue] = useState("");
  const [parentId, setParentId] = useState<string>("");
  const [error, setError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const touch = () => {
    if (!createFromStockView) setTouch(true);
  };

  useEffect(() => {
    if (isEdit && selectedId) {
      const location = locations.find((l) => l.id === selectedId);
      if (location) {
        setNameValue(location.name);
        // Keep the current parent so saving doesn't silently move it to root.
        setParentId(location.parent_id ?? "");
      }
    } else if (context === DRAWER_CONTEXT.CREATE_LOCATION && prefillLocationId) {
      // Opened from inside a folder — default the parent to it.
      setParentId(prefillLocationId);
    }
  }, [context, isEdit, selectedId, prefillLocationId, locations]);

  // Exclude self + descendants when editing (no cycles).
  const parentOptions = useMemo(
    () => buildParentOptions(locations, isEdit ? selectedId ?? undefined : undefined),
    [locations, isEdit, selectedId]
  );

  const selectedParent = useMemo(
    () => locations.find((l) => l.id === parentId) ?? null,
    [locations, parentId]
  );

  const setName = (value: string) => {
    setNameValue(value);
    setError(undefined);
    touch();
  };
  const setParent = (id: string) => {
    setParentId(id);
    touch();
  };

  const submit = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit && selectedId) {
        await updateLocation.mutateAsync({
          id: selectedId,
          name,
          organization_id: baseUser!.organization_id!,
          parent_id: parentId || null,
        });
        onInfoShow("Location updated successfully");
      } else {
        await createLocation.mutateAsync({
          name,
          organization_id: baseUser!.organization_id!,
          parent_id: parentId || null,
        });
        onInfoShow("Location created successfully");
      }
      setTouch(false);
      close();
    } catch {
      // Global mutation handler maps + shows the error; keep the drawer open.
    } finally {
      setSubmitting(false);
    }
  };

  return {
    isEdit,
    name,
    parentId,
    selectedParent,
    parentOptions,
    error,
    submitting,
    setName,
    setParent,
    submit,
  };
}
